import React, { useState, useEffect, useRef } from "react";
import Loader from "../../../../components/loader/loader";
import Snackbar from "../../../../components/snackbar/snackbar";
import "./style.css";
import moment from "moment";
import {
  internshipQualifications,
  submitInternshipProposal,
  updateInternshipProposal,
  internshipRequestDetail,
} from "../../user.api";
import BreadCrumpBack from "../../../../components/navigator/back";

export default function InternshipProposalForm(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [parentQuals, setParentQuals] = useState([]);
  const [childQuals, setChildQuals] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState("");
  const [subTypeGroups, setSubTypeGroups] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    target_category: "students_undergoing_course",
    internship_discipline: "",
    brief_description: "",
    number_of_students: "",
    preferred_start_date: "",
    preferred_end_date: "",
    additional_certifications: "",
    grade_criteria: "",
  });

  const [errors, setErrors] = useState({});
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const formRef = useRef(null);

  const snackbarClose = (flag) => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
  };

  const showSnackbar = (message, isError = false) => {
    setSnackMessage(message);
    setIsSnackError(isError);
    setSnackOpen(true);
  };

  useEffect(() => {
    // Check if we're in edit mode from URL query parameter
    const searchParams = new URLSearchParams(window.location.search);
    const editId = searchParams.get("edit");

    if (editId) {
      setIsEditMode(true);
      setEditId(editId);
      // Fetch the proposal data
      fetchProposalForEdit(editId);
    }

    fetchQualifications();
  }, []);

  const fetchQualifications = () => {
    setIsLoading(true);
    internshipQualifications()
      .then((res) => {
        const data = res?.data || [];
        setParentQuals(data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch qualifications:", err);
        showSnackbar("Failed to fetch qualifications", true);
        setIsLoading(false);
      });
  };

  const fetchProposalForEdit = (proposalId) => {
    setIsLoading(true);
    internshipRequestDetail(proposalId)
      .then((res) => {
        if (res?.success && res?.data) {
          populateEditFormData(res.data);
          setIsLoading(false);
        } else {
          showSnackbar("Failed to load proposal for editing", true);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.error("Error fetching proposal for edit:", err);
        showSnackbar("Error loading proposal for editing", true);
        setIsLoading(false);
      });
  };

  const populateEditFormData = (editData) => {
    if (!editData) return;

    // Set basic form data
    setFormData({
      target_category: editData.target_category || "students_undergoing_course",
      internship_discipline: editData.internship_discipline || "",
      brief_description: editData.brief_description || "",
      number_of_students: editData.number_of_students || "",
      preferred_start_date: editData.preferred_start_date ? moment(editData.preferred_start_date).format("YYYY-MM-DD") : "",
      preferred_end_date: editData.preferred_end_date ? moment(editData.preferred_end_date).format("YYYY-MM-DD") : "",
      additional_certifications: editData.additional_certifications || "",
      grade_criteria: editData.grade_criteria || "",
    });

    // Set selected parent qualification type
    const parentId = editData.qualification_type?.id || editData.qualification_type;
    setSelectedParentId(parentId);

    // Store the IDs for later use when childQuals are loaded
    // They will be reconstructed after fetching qualifications
    window.editProposalData = {
      qualification_type_parents: editData.qualification_type_parents || [],
      qualification_type_subs: editData.qualification_type_subs || [],
    };
  };

  // Effect to reconstruct subTypeGroups after parentQuals are loaded
  useEffect(() => {
    if (isEditMode && selectedParentId && parentQuals.length > 0 && window.editProposalData) {
      const selected = parentQuals.find((q) => q.id === selectedParentId);

      if (selected && selected.children_recursive) {
        setChildQuals(selected.children_recursive);

        // Reconstruct subTypeGroups from saved data
        const parentIds = window.editProposalData.qualification_type_parents;
        const subIds = window.editProposalData.qualification_type_subs;

        if (parentIds && parentIds.length > 0) {
          const reconstructedGroups = parentIds.map((parentId) => {
            const parentQual = selected.children_recursive.find(
              (q) => q.id === parentId,
            );

            return {
              subTypeId: parentId,
              subTypeName: parentQual?.name || "",
              availableQuals:
                parentQual && parentQual.children_recursive
                  ? parentQual.children_recursive
                  : [],
              selectedQualIds: Array.isArray(subIds) ? subIds : [subIds],
            };
          });

          setSubTypeGroups(reconstructedGroups);
          delete window.editProposalData;
        }
      }
    }
  }, [isEditMode, selectedParentId, parentQuals]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "number_of_students") {
      if (value && parseInt(value) > 500) {
        newValue = "500";
      }
    }
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleParentQualChange = (e) => {
    const selectedId = parseInt(e.target.value);
    const selected = parentQuals.find((q) => q.id === selectedId);

    setSelectedParentId(selectedId);
    if (selected && selected.children_recursive) {
      setChildQuals(selected.children_recursive);
    } else {
      setChildQuals([]);
    }
    setSubTypeGroups([]);
  };

  const addNewSubTypeGroup = () => {
    if (!selectedParentId) {
      showSnackbar("Please select a Qualification Type first", true);
      return;
    }

    setSubTypeGroups((prev) => [
      ...prev,
      {
        subTypeId: "",
        subTypeName: "",
        availableQuals: [],
        selectedQualIds: [],
      },
    ]);
  };

  const removeSubTypeGroup = (index) => {
    setSubTypeGroups((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubTypeChange = (index, subTypeId) => {
    const selectedSubType = childQuals.find(
      (q) => q.id === parseInt(subTypeId),
    );

    setSubTypeGroups((prev) => {
      const updated = [...prev];
      updated[index] = {
        subTypeId: parseInt(subTypeId),
        subTypeName: selectedSubType?.name || "",
        availableQuals:
          selectedSubType && selectedSubType.children_recursive
            ? selectedSubType.children_recursive
            : [],
        selectedQualIds: [],
      };
      return updated;
    });
  };

  const toggleQualificationTag = (groupIndex, qualId) => {
    setSubTypeGroups((prev) => {
      const updated = [...prev];
      const currentIds = updated[groupIndex].selectedQualIds;
      let newIds;
      if (currentIds.includes(qualId)) {
        newIds = currentIds.filter((id) => id !== qualId);
      } else {
        newIds = [...currentIds, qualId];
      }
      updated[groupIndex] = {
        ...updated[groupIndex],
        selectedQualIds: newIds,
      };
      return updated;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.internship_discipline.trim()) {
      newErrors.internship_discipline = "Internship discipline is required";
    }
    if (!formData.brief_description.trim()) {
      newErrors.brief_description = "Brief description is required";
    }
    if (!formData.number_of_students || parseInt(formData.number_of_students) <= 0) {
      newErrors.number_of_students = "Number of positions must be at least 1";
    }
    if (!formData.preferred_start_date) {
      newErrors.preferred_start_date = "Start date is required";
    } else if (moment(formData.preferred_start_date).isBefore(moment().add(1, 'days'), 'day')) {
      newErrors.preferred_start_date = "Start date must be from tomorrow onwards";
    }

    if (!formData.preferred_end_date) {
      newErrors.preferred_end_date = "End date is required";
    } else if (moment(formData.preferred_end_date).isSameOrBefore(moment(formData.preferred_start_date))) {
      newErrors.preferred_end_date = "End date must be after the start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showSnackbar("Please fill all required fields", true);
      return;
    }

    if (!selectedParentId) {
      showSnackbar("Please select a Qualification Type", true);
      return;
    }

    if (childQuals && childQuals.length > 0) {
      if (subTypeGroups.length === 0) {
        showSnackbar("Please add at least one stream", true);
        return;
      }

      const isInvalid = subTypeGroups.some((g) => {
        // Must have selected a stream type
        if (!g.subTypeId) return true;
        // If specific qualifications are available, must select at least one
        if (g.availableQuals && g.availableQuals.length > 0) {
          return g.selectedQualIds.length === 0;
        }
        return false;
      });

      if (isInvalid) {
        showSnackbar(
          "Please select a stream and its specific qualifications if available",
          true,
        );
        return;
      }
    }

    setIsLoading(true);

    try {
      // Collect all unique subTypeIds and qualificationIds
      const allSubTypeIds = subTypeGroups
        .map((group) => group.subTypeId)
        .filter((id) => id);

      const allQualificationIds = subTypeGroups
        .flatMap((group) => group.selectedQualIds)
        .filter((id) => id);

      // Get unique parent IDs from childQuals where id matches subTypeIds
      const parentIds = [
        ...new Set(
          subTypeGroups
            .filter((group) => group.subTypeId)
            .map((group) => {
              const subType = childQuals.find((q) => q.id === group.subTypeId);
              return subType?.parent_id;
            })
            .filter((id) => id)
        ),
      ];

      // Prepare payload with correct format
      const payload = {
        target_category: formData.target_category,
        internship_discipline: formData.internship_discipline,
        brief_description: formData.brief_description,
        number_of_students: parseInt(formData.number_of_students),
        preferred_start_date: formData.preferred_start_date,
        preferred_end_date: formData.preferred_end_date,
        additional_certifications: formData.additional_certifications,
        grade_criteria: formData.grade_criteria,
        qualification_type: selectedParentId,
        qualification_type_parents: allSubTypeIds,
        qualification_type_subs: allQualificationIds,
      };

      let res;
      if (isEditMode && editId) {
        // Use update endpoint for edit mode
        res = await updateInternshipProposal(editId, payload);
      } else {
        // Use create endpoint for new proposal
        res = await submitInternshipProposal(payload);
      }

      console.log("Submit response:", res);

      if (res?.success && res?.message) {
        console.log("Success:", res.message);
        showSnackbar(res.message, false);
        // Redirect after a short delay
        setTimeout(() => {
          props.history.push("/internship-proposals");
        }, 1500);
      } else {
        showSnackbar(res?.message || "Failed to submit internship proposal", true);
      }
    } catch (error) {
      console.error("Error submitting proposal:", error);
      showSnackbar(
        error?.response?.data?.message ||
        "Failed to submit internship proposal",
        true,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const categories = [
    {
      value: "students_undergoing_course",
      label: "Students Currently Enrolled in Course",
    },
    {
      value: "students_completed_course",
      label: "Students Who Have Completed the Course",
    },
    { value: "both", label: "All Students (Enrolled & Completed)" },
  ];

  return (
    <div className="internship-form-page" ref={formRef}>
      <Loader open={isLoading} />
      <div className="dash-banner">
        <div className="tit-txt">
          {isEditMode ? (
            props.state.language === "ma"
              ? "ഇന്റേൺഷിപ്പ് പ്രൊപ്പോസൽ എഡിറ്റ് ചെയ്യുക"
              : "Edit Internship Proposal"
          ) : (
            props.state.language === "ma"
              ? "പുതിയ ഇന്റേൺഷിപ്പ് പ്രൊപ്പോസൽ സ്ഥാപിക്കുക"
              : "Create New Internship Proposal"
          )}
        </div>
      </div>
      <BreadCrumpBack />

      <div className="container mt-5 mb-5">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="form-container">
              <form onSubmit={handleSubmit} className="internship-form">
                <h5 className="form-section-title mb-4">
                  {props.state.language === "ma"
                    ? "ഇന്റേൺഷിപ്പ് പ്രൊപ്പോസൽ വിശദാംശങ്ങൾ"
                    : "Internship Proposal Details"}
                </h5>

                {/* Target Category */}
                <div className="form-group mb-4">
                  <label className="form-label">
                    Target Category <span className="text-danger">*</span>
                  </label>
                  <select
                    name="target_category"
                    className="form-select"
                    value={formData.target_category}
                    onChange={handleInputChange}
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Internship Discipline */}
                <div className="form-group mb-4">
                  <label className="form-label">
                    Internship Discipline <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="internship_discipline"
                    className={`form-control ${errors.internship_discipline ? "is-invalid" : ""
                      }`}
                    value={formData.internship_discipline}
                    onChange={handleInputChange}
                    placeholder="e.g., Software Development, Mechanical Engineering"
                  />
                  {errors.internship_discipline && (
                    <div className="invalid-feedback d-block">
                      {errors.internship_discipline}
                    </div>
                  )}
                </div>

                {/* Brief Description */}
                <div className="form-group mb-4">
                  <label className="form-label">
                    Brief Description <span className="text-danger">*</span>
                  </label>
                  <textarea
                    name="brief_description"
                    className={`form-control ${errors.brief_description ? "is-invalid" : ""
                      }`}
                    rows="4"
                    value={formData.brief_description}
                    onChange={handleInputChange}
                    placeholder="Describe the internship opportunity"
                  ></textarea>
                  {errors.brief_description && (
                    <div className="invalid-feedback d-block">
                      {errors.brief_description}
                    </div>
                  )}
                </div>

                {/* Number of Students */}
                <div className="form-group mb-4">
                  <label className="form-label">
                    Number of Positions <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    name="number_of_students"
                    className={`form-control ${errors.number_of_students ? "is-invalid" : ""
                      }`}
                    value={formData.number_of_students}
                    onChange={handleInputChange}
                    placeholder="e.g., 5"
                    min="1"
                    max="500"
                  />
                  {errors.number_of_students && (
                    <div className="invalid-feedback d-block">
                      {errors.number_of_students}
                    </div>
                  )}
                </div>

                {/* Dates */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="form-label">
                        Preferred Start Date{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        name="preferred_start_date"
                        className={`form-control ${errors.preferred_start_date ? "is-invalid" : ""
                          }`}
                        value={formData.preferred_start_date}
                        onChange={handleInputChange}
                        min={moment().add(1, 'days').format('YYYY-MM-DD')}
                      />
                      {errors.preferred_start_date && (
                        <div className="invalid-feedback d-block">
                          {errors.preferred_start_date}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="form-label">
                        Preferred End Date{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        name="preferred_end_date"
                        className={`form-control ${errors.preferred_end_date ? "is-invalid" : ""
                          }`}
                        value={formData.preferred_end_date}
                        onChange={handleInputChange}
                        min={formData.preferred_start_date || moment().add(2, 'days').format('YYYY-MM-DD')}
                      />
                      {errors.preferred_end_date && (
                        <div className="invalid-feedback d-block">
                          {errors.preferred_end_date}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Grade Criteria */}
                <div className="form-group mb-4">
                  <label className="form-label">Grade Criteria</label>
                  <input
                    type="text"
                    name="grade_criteria"
                    className="form-control"
                    value={formData.grade_criteria}
                    onChange={handleInputChange}
                    placeholder="e.g., Minimum 7.0 CGPA"
                  />
                </div>

                {/* Additional Certifications */}
                <div className="form-group mb-4">
                  <label className="form-label">
                    Additional Certifications/Skills
                  </label>
                  <textarea
                    name="additional_certifications"
                    className="form-control"
                    rows="3"
                    value={formData.additional_certifications}
                    onChange={handleInputChange}
                    placeholder="e.g., JavaScript, React, Node.js knowledge preferred"
                  ></textarea>
                </div>

                {/* Qualifications Section */}
                <h5 className="form-section-title mb-4 mt-5">
                  {props.state.language === "ma"
                    ? "ആവശ്യമായ യോഗ്യതകൾ"
                    : "Required Qualifications"}
                </h5>

                {/* Main Qualification Type - Single Selection */}
                <div className="form-group mb-4">
                  <label className="form-label">
                    Qualification Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={selectedParentId || ""}
                    onChange={handleParentQualChange}
                  >
                    <option value="" disabled>-- Select Qualification Type --</option>
                    {parentQuals.map((qual) => (
                      <option key={qual.id} value={qual.id}>
                        {qual.name}
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    You can only select one Qualification Type, but can add
                    multiple Streams
                  </small>
                </div>

                {/* Qualification Sub-Type Groups */}
                {selectedParentId && childQuals.length > 0 && (
                  <>
                    <div className="qualifications-container">
                      {subTypeGroups.map((group, groupIndex) => (
                        <div
                          key={groupIndex}
                          className="qualification-group mb-4 p-4 border rounded"
                        >
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">
                              Qualification{groupIndex + 1}
                            </h6>
                            {subTypeGroups.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => removeSubTypeGroup(groupIndex)}
                              >
                                Remove Stream
                              </button>
                            )}
                          </div>

                          {/* Sub-Type Selection */}
                          <div className="form-group mb-4">
                            <label className="form-label">
                              Select Stream{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              value={group.subTypeId || ""}
                              onChange={(e) =>
                                handleSubTypeChange(groupIndex, e.target.value)
                              }
                            >
                              <option value="">-- Select Stream --</option>
                              {childQuals.map((qual) => (
                                <option key={qual.id} value={qual.id}>
                                  {qual.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Specific Qualifications as Tags */}
                          {group.subTypeId &&
                            group.availableQuals.length > 0 && (
                              <div className="form-group">
                                <label className="form-label">
                                  Select Specific Qualifications{" "}
                                  <span className="text-danger">*</span>
                                </label>

                                {/* Selected Qualifications Tags */}
                                <div className="qualifications-tags-container">
                                  {group.selectedQualIds.length > 0 ? (
                                    group.selectedQualIds.map((qualId) => {
                                      const qual = group.availableQuals.find(
                                        (q) => q.id === qualId,
                                      );
                                      return qual ? (
                                        <span
                                          key={qualId}
                                          className="qualification-tag"
                                        >
                                          {qual.name}
                                          <button
                                            type="button"
                                            className="tag-remove"
                                            onClick={() =>
                                              toggleQualificationTag(
                                                groupIndex,
                                                qualId,
                                              )
                                            }
                                          >
                                            ×
                                          </button>
                                        </span>
                                      ) : null;
                                    })
                                  ) : (
                                    <p className="text-muted small">
                                      No qualifications selected
                                    </p>
                                  )}
                                </div>

                                {/* Available Qualifications Pills */}
                                <div className="available-qualifications mt-3">
                                  <small className="d-block mb-2 text-muted">
                                    Click to add qualifications:
                                  </small>
                                  <div className="qualifications-list">
                                    {group.availableQuals.map((qual) => (
                                      <button
                                        key={qual.id}
                                        type="button"
                                        className={`qualification-pill ${group.selectedQualIds.includes(
                                          qual.id,
                                        )
                                          ? "active"
                                          : ""
                                          }`}
                                        onClick={() =>
                                          toggleQualificationTag(
                                            groupIndex,
                                            qual.id,
                                          )
                                        }
                                      >
                                        {qual.name}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                        </div>
                      ))}
                    </div>

                    {/* Add New Sub-Type Button */}
                    {childQuals.length > 0 && (
                      <div className="mb-4">
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={addNewSubTypeGroup}
                        >
                          + Add Another Qualification Stream
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* Form Actions */}
                <div className="form-actions mt-5">
                  <button
                    type="submit"
                    className="btn btn-primary me-2"
                    disabled={isLoading}
                  >
                    {isLoading
                      ? isEditMode
                        ? "Updating..."
                        : "Submitting..."
                      : isEditMode
                        ? props.state.language === "ma"
                          ? "അപ്ഡേറ്റ് ചെയ്യുക"
                          : "Update Proposal"
                        : props.state.language === "ma"
                          ? "സമർപ്പിക്കുക"
                          : "Submit Proposal"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => props.history.push("/internship-proposals")}
                    disabled={isLoading}
                  >
                    {props.state.language === "ma" ? "റദ്ദാക്കുക" : "Cancel"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />
    </div>
  );
}
